import { authHeader } from '../helpers/authHeader';
import { handleAxiosResponse, api_base_url, axiosInstance } from '../services/common.services';
import NotificationService from "./NotificationService";

interface AxiosRequestConfig {
    url: string;
    headers?: Record<string, string>;
    params?: Record<string, any>;
    body?: any;
    postBody?: any;
    patchBody?: any;
    convertFunction?: (response: any) => any;
    customHandleError?: (error: any) => any;
    customErrorMessage?: string;
    onCompleteFunction?: () => void;
    filename?: string;
    ext?: string;
}

const handleError = (error: any, customHandleError?: (error: any) => any, customErrorMessage?: string) => {
    console.log(error)
    if (customHandleError) {
        return customHandleError(error);
    } else if(error?.response?.data?.problemDetails?.detail){
        return NotificationService.error(error.response.data.problemDetails.detail)
    } else if (customErrorMessage) {
        return NotificationService.error(customErrorMessage);
    } 
    else {
        return NotificationService.handleError(error);
    }
};

const get = ({
                 url,
                 convertFunction,
                 customHandleError,
                 customErrorMessage,
                 headers = {},
                 params = {},
             }: AxiosRequestConfig): Promise<any> => {
    return axiosInstance
        .get(`${api_base_url}${url}`, {
            headers: { ...authHeader(), ...headers },
            params,
        })
        .then(handleAxiosResponse)
        .then(res => {
            return convertFunction ? convertFunction(res) : res;
        })
        .catch(error => handleError(error, customHandleError, customErrorMessage));
};

const getWithParams = ({
                           url,
                           params,
                           convertFunction,
                           customHandleError,
                           customErrorMessage,
                       }: AxiosRequestConfig): Promise<any> => {
    return axiosInstance
        .get(`${api_base_url}${url}`, {
            headers: authHeader(),
            params,
        })
        .then(handleAxiosResponse)
        .then(res => {
            return convertFunction ? convertFunction(res) : res;
        })
        .catch(error => handleError(error, customHandleError, customErrorMessage));
};

const getWithBody = ({
                         url,
                         body,
                         convertFunction,
                         customHandleError,
                         customErrorMessage,
                     }: AxiosRequestConfig): Promise<any> => {
    return axiosInstance
        .get(
            `${api_base_url}${url}`,
            {
                headers: authHeader(),
                data: body,  // 'data' for the body in GET request
            }
        )
        .then(handleAxiosResponse)
        .then(res => {
            return convertFunction ? convertFunction(res) : res;
        })
        .catch(error => handleError(error, customHandleError, customErrorMessage));
};

const post = ({
                  url,
                  postBody,
                  convertFunction,
                  onCompleteFunction,
                  customHandleError,
                  customErrorMessage,
                  headers,
              }: AxiosRequestConfig): Promise<any> => {
    return axiosInstance
        .post(`${api_base_url}${url}`, postBody, {
            headers: { ...authHeader(), ...headers },
        })
        .then(handleAxiosResponse)
        .then(res => {
            if (convertFunction) {
                return convertFunction(res);
            } else if (onCompleteFunction) {
                onCompleteFunction();
                return res;
            } else {
                return res;
            }
        })
        .catch(error => handleError(error, customHandleError, customErrorMessage));
};

const put = ({
                 url,
                 postBody,
                 convertFunction,
                 onCompleteFunction,
                 customHandleError,
                 customErrorMessage,
                 headers,
             }: AxiosRequestConfig): Promise<any> => {
    return axiosInstance
        .put(`${api_base_url}${url}`, postBody, {
            headers: { ...authHeader(), ...headers },
        })
        .then(handleAxiosResponse)
        .then(res => {
            if (convertFunction) {
                return convertFunction(res);
            } else if (onCompleteFunction) {
                onCompleteFunction();
                return res;
            } else {
                return res;
            }
        })
        .catch(error => handleError(error, customHandleError, customErrorMessage));
};

const patch = ({
                   url,
                   patchBody,
                   convertFunction,
                   onCompleteFunction,
                   customHandleError,
                   customErrorMessage,
               }: AxiosRequestConfig): Promise<any> => {
    return axiosInstance
        .patch(`${api_base_url}${url}`, patchBody, {
            headers: authHeader(),
        })
        .then(handleAxiosResponse)
        .then(res => {
            if (convertFunction) {
                return convertFunction(res);
            } else if (onCompleteFunction) {
                onCompleteFunction();
                return res;
            } else {
                return res;
            }
        })
        .catch(error => handleError(error, customHandleError, customErrorMessage));
};

const deleteItem = ({
                        url,
                        onCompleteFunction,
                        customHandleError,
                        customErrorMessage,
                    }: AxiosRequestConfig): Promise<any> => {
    return axiosInstance
        .delete(`${api_base_url}${url}`, {
            headers: authHeader(),
        })
        .then(res => {
            if (onCompleteFunction) {
                onCompleteFunction();
                return res;
            } else {
                return res;
            }
        })
        .catch(error => handleError(error, customHandleError, customErrorMessage));
};

const download = ({
                      url,
                      filename,
                      ext = '',
                      customErrorMessage,
                      customHandleError,
                  }: AxiosRequestConfig): Promise<void> => {
    return axiosInstance
        .get(`${api_base_url}${url}`, {
            headers: authHeader(),
            responseType: 'blob',
        })
        .then(res => {
            const type = res.headers['content-type'];
            const blob = new Blob([res.data], { type: type});
            let backendFilename = filename + ext;
            const contentDisposition = res.headers['content-disposition'];
            console.log(contentDisposition)
            if (contentDisposition) {
                const utf8FilenameRegex = /filename\*\=UTF-8''(.+)/i;
                const asciiFilenameRegex = /filename=\"?([^\";]+)\"?/i;

                const utf8Match = utf8FilenameRegex.exec(contentDisposition);
                if (utf8Match && utf8Match[1]) {
                    backendFilename = decodeURIComponent(utf8Match[1]);
                } else {
                    const asciiMatch = asciiFilenameRegex.exec(contentDisposition);
                    if (asciiMatch && asciiMatch[1]) {
                        backendFilename = asciiMatch[1];
                    }
                }
            }
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = backendFilename
            link.click();
            link.remove();
        })
        .catch(error => handleError(error, customHandleError, customErrorMessage));
};

export const handleServiceRequest = {
    get,
    getWithBody,
    getWithParams,
    post,
    deleteItem,
    patch,
    put,
    download,
};
